import { ref } from 'vue'

import { nanoid } from 'nanoid'
import { defineStore } from 'pinia'

import type { IAlert, IAlertOptions } from '@/views/components/ui/alerts/types'
import { ALERT_TYPE } from '@/views/components/ui/alerts/types'

export const useAlert = defineStore('alert', () => {
  const alerts = ref<IAlert[]>([])

  const addAlert = (
    title: string,
    message: string,
    options: IAlertOptions = {},
  ) => {
    const alertId = nanoid()
    alerts.value.push({
      id: alertId,
      title,
      message,
      ...options,
      type: options.type || ALERT_TYPE.INFO,
    })
    if (options.timeout === 'none') return

    setTimeout(() => {
      removeAlert(alertId)
    }, options.timeout || 30000000)

    return alertId
  }

  const removeAlert = (alertId: string) => {
    alerts.value = alerts.value.filter(({ id }) => id !== alertId)
  }

  const onSuccess = (
    title: string,
    message: string,
    options: IAlertOptions = {},
  ) =>
    addAlert(title, message, {
      type: ALERT_TYPE.SUCCESS,
      ...options,
    })
  const onError = (
    title: string,
    message: string,
    options: IAlertOptions = {},
  ) =>
    addAlert(title, message, {
      type: ALERT_TYPE.ERROR,
      ...options,
    })
  const onWarning = (
    title: string,
    message: string,
    options: IAlertOptions = {},
  ) =>
    addAlert(title, message, {
      type: ALERT_TYPE.WARNING,
      ...options,
    })
  const onCaution = (
    title: string,
    message: string,
    options: IAlertOptions = {},
  ) =>
    addAlert(title, message, {
      type: ALERT_TYPE.CAUTION,
      ...options,
    })
  const onInfo = (
    title: string,
    message: string,
    options: IAlertOptions = {},
  ) =>
    addAlert(title, message, {
      type: ALERT_TYPE.INFO,
      ...options,
    })
  const onBasic = (
    title: string,
    message: string,
    options: IAlertOptions = {},
  ) =>
    addAlert(title, message, {
      type: ALERT_TYPE.BASIC,
      ...options,
    })

  const clearAlerts = () => {
    alerts.value = []
  }

  return {
    onSuccess,
    onError,
    onWarning,
    onCaution,
    onInfo,
    onBasic,
    alerts,
    removeAlert,
    clearAlerts,
  }
})
